import type { ArtTheme } from './covers/kit';
import {
  RASTER_ART,
  RASTER_ATLASES,
  RASTER_BACKGROUNDS,
  RASTER_BG,
  RASTER_SLICE,
  RASTER_SYMBOLS,
  type RasterArtKeys,
  type RasterAtlas,
  type RasterBgKeys,
  type RasterGroup,
  type RasterImage,
  type RasterSlot,
  type RasterSymbolKey,
} from './raster.gen';

/*
 * Растр слотов из pnpm art:process (scripts/art/process.mjs → raster.gen.ts). Пока ключа нет в карте —
 * вызывающие остаются на векторных заглушках (symbols.ts). Символы растра одни на обе темы, фоны — по теме.
 */
export type {
  RasterArtKeys,
  RasterAtlas,
  RasterBgKeys,
  RasterGroup,
  RasterImage,
  RasterSlot,
  RasterSymbolKey,
};

/** 1 — обычный экран, 2 — @2x (devicePixelRatio > 1). */
export type RasterScale = 1 | 2;

export function currentScale(): RasterScale {
  // globalThis, not window: the art modules are also compiled by the server (share cards) without DOM types
  return ((globalThis as { devicePixelRatio?: number }).devicePixelRatio || 1) > 1 ? 2 : 1;
}

const pick = (img: RasterImage, scale: RasterScale) => (scale === 2 ? img.x2 : img.x1);

/** Ключи символов уникальны между слотами → плоский индекс имя → пара путей. */
const BY_NAME = new Map<string, RasterImage>();
for (const slot of Object.values(RASTER_SYMBOLS))
  for (const [key, img] of Object.entries(slot ?? {})) if (img) BY_NAME.set(key, img);

/**
 * Lever machines keep their symbols as objects of their own group (GROUPS['lucky-lever'].objects: peach, ingot …);
 * the vector art names them with the machine's prefix (llPeach, llIngot …). A group's set is in RASTER_ART only when
 * complete (process.mjs `sets`), so the reels never mix raster and vector symbols.
 */
export const GROUP_SYMBOL_PREFIX: Partial<Record<RasterGroup, string>> = {
  'lucky-lever': 'll',
  'neon-arcade': 'na',
  'pirate-cove': 'pc',
};
export const groupSymbolName = (prefix: string, key: string): string =>
  prefix + key.charAt(0).toUpperCase() + key.slice(1);
for (const [group, prefix] of Object.entries(GROUP_SYMBOL_PREFIX) as [RasterGroup, string][])
  for (const [key, img] of Object.entries(RASTER_ART[group] ?? {}))
    if (img) BY_NAME.set(groupSymbolName(prefix, key), img as RasterImage);

/** URL растра символа слота (в т.ч. jar_mini/jar_major/jar_grand) или null, если его ещё нет. */
export function rasterSymbol(slot: RasterSlot, key: RasterSymbolKey, scale = currentScale()): string | null {
  const img = RASTER_SYMBOLS[slot]?.[key];
  return img ? pick(img, scale) : null;
}

/** То же по имени символа без слота (как symbolUri). */
export function rasterSymbolByName(name: string, scale = currentScale()): string | null {
  const img = BY_NAME.get(name);
  return img ? pick(img, scale) : null;
}

/**
 * Фон барабанов (`reels`, пропорция сетки слота) или сцены (`scene`, 16:9) для темы; null — растра нет,
 * оставить нынешний CSS-фон.
 */
export function slotBackground(
  slot: RasterSlot,
  kind: 'reels' | 'scene',
  theme: ArtTheme,
  scale = currentScale(),
): string | null {
  const img = RASTER_BACKGROUNDS[slot]?.[`${kind}-${theme}`];
  return img ? pick(img, scale) : null;
}

/** Атлас символов слота (координаты кадров в 1x; для x2 — умножить на 2). */
export function slotAtlas(slot: RasterSlot): RasterAtlas | null {
  return RASTER_ATLASES[slot] ?? null;
}

/*
 * Other games (scripts/art/process.mjs GROUPS): lobby covers, objects, icons — one edition for both themes;
 * backgrounds per theme. null → the caller keeps its SVG / CSS art.
 */

/** URL of a cover / object / icon of a group, or null if it is not there yet. */
export function rasterArt<G extends RasterGroup>(
  group: G,
  key: RasterArtKeys[G],
  scale = currentScale(),
): string | null {
  const img = (RASTER_ART[group] as Partial<Record<RasterArtKeys[G], RasterImage>> | undefined)?.[key];
  return img ? pick(img, scale) : null;
}

/** URL of a themed background (`<key>-dark|light`) of a group, or null. */
export function rasterBg<G extends keyof RasterBgKeys>(
  group: G,
  key: RasterBgKeys[G],
  theme: ArtTheme,
  scale = currentScale(),
): string | null {
  const bases = RASTER_BG[group] as
    Partial<Record<RasterBgKeys[G], Partial<Record<ArtTheme, RasterImage>>>> | undefined;
  const img = bases?.[key]?.[theme];
  return img ? pick(img, scale) : null;
}

/** An icon (ADDENDUM §6, 64 / @2x 128 px) — the UI draws it through `UiIcon`, with its own glyph as the fallback. */
export const rasterIcon = (key: RasterIconFile, scale = currentScale()) => rasterArt('icons', key, scale);

/** every icon file, the simplified small editions (`<key>-small`) included */
type RasterIconFile = RasterArtKeys['icons'];
/** a UI icon by name; its small edition is picked by size (`iconAt`), never named directly */
export type IconKey = Exclude<RasterIconFile, `${string}-small`>;

/**
 * Smallest CSS size (px) at which a raster icon still reads (checked at 16/20/24 px on both themes). The detailed
 * pictures — people, a fan of cards, the autoplay arrows around a button, the clock of history, the door of logout,
 * the sun-and-moon — turn into a blot below it, so smaller spots take the simplified small edition
 * (`icons/<key>-small`, docs/art-codex-task-v4.md P4) when Codex drew one, else keep their vector glyph. Default: 16.
 */
export const ICON_MIN_PX: Partial<Record<IconKey, number>> = {
  games: 32,
  friends: 32,
  profile: 32,
  autoplay: 28,
  history: 28,
  logout: 28,
  theme: 28,
  slots: 24,
  home: 24,
  // two bubbles with dots and a slot machine with three windows: their small editions read better in tabs / captions
  messages: 28,
  machines: 24,
};

/**
 * URL of the icon for a spot of `px` CSS pixels: the icon itself from the size it reads at, below that its small
 * edition if there is one; null → keep the glyph (no raster, or too small to read and no small edition).
 */
export function iconAt(key: IconKey, px: number, scale = currentScale()): string | null {
  // 64 px covers up to 32 CSS px at 2x; larger spots take the 128 px file
  const res = px * scale > 64 ? 2 : 1;
  if (px >= (ICON_MIN_PX[key] ?? 16)) return rasterIcon(key, res);
  return rasterIcon(`${key}-small` as RasterIconFile, res);
}

/** Pieces of the Codex UI kit taken into the game (RASTER_BG.ui). */
export type UiPiece = RasterBgKeys['ui'];

/** A themed UI piece (reels frame, jackpot / win plate, lobby badge) or null. */
export const rasterUi = (key: UiPiece, theme: ArtTheme, scale = currentScale()) =>
  rasterBg('ui', key, theme, scale);

/** `border-image-slice` of a 9-slice UI piece in % of its size (vertical, horizontal) + `fill`, or null. */
export function uiSlice(key: UiPiece): string | null {
  const sl = RASTER_SLICE.ui?.[key];
  if (!sl) return null;
  const pct = (n: number) => `${+((sl.slice / n) * 100).toFixed(3)}%`;
  return `${pct(sl.height)} ${pct(sl.width)} fill`;
}

/** `border-image-slice` of a 9-slice themed piece of any group (lever cabinets) — like `uiSlice`. */
export function groupSlice<G extends keyof RasterBgKeys>(group: G, key: RasterBgKeys[G]): string | null {
  const sl = (
    RASTER_SLICE[group] as
      Partial<Record<RasterBgKeys[G], { slice: number; width: number; height: number }>> | undefined
  )?.[key];
  if (!sl) return null;
  const pct = (n: number) => `${+((sl.slice / n) * 100).toFixed(3)}%`;
  return `${pct(sl.height)} ${pct(sl.width)} fill`;
}

/**
 * CSS variables of a 9-slice UI piece for `style`: `--<name>` (url) and `--<name>-slice`; empty without the raster —
 * pair it with a `data-*` attribute so the CSS switches only when the image exists.
 */
export function uiPieceVars(name: string, key: UiPiece, theme: ArtTheme): Record<string, string> {
  const url = rasterUi(key, theme);
  const slice = uiSlice(key);
  return url && slice ? { [`--${name}`]: `url("${url}")`, [`--${name}-slice`]: slice } : {};
}

/**
 * CSS custom property with a background URL for `style` (`{ '--name': 'url("…")' }`), empty when there is no raster;
 * pair it with a `data-*` attribute so the CSS switches only when the image exists.
 */
export function bgVar(name: string, url: string | null): Record<string, string> {
  return url ? { [name]: `url("${url}")` } : {};
}
